import { BOARD_W, BOARD_H, geometry, pathFor, flightPoint } from './plinkoModel';

// Presents only a paid, validated server path. It never draws an outcome or
// changes a wallet. The peg materials, bounce curve and pocket alignment are
// carried over from the approved playable preview.
export function createPlinkoPresenter(canvas, rows = 12, onBounce) {
 let ctx; try { ctx = canvas.getContext('2d'); } catch { ctx = null; }
 const unavailable = () => Promise.reject(new Error('Canvas animation is unavailable.'));
 if (!ctx) return { available: false, setRows() {}, drop: unavailable, cancel() {} };
 const density = Math.min(2, Math.max(1, window.devicePixelRatio || 1));
 canvas.width = BOARD_W * density; canvas.height = BOARD_H * density; ctx.setTransform(density, 0, 0, density, 0, 0);
 const media = window.matchMedia?.('(prefers-reduced-motion: reduce)');
 const reduced = () => !!media?.matches;
 const game = { rows }; let visuals = [], impacts = [], landings = [], raf = 0, disposed = false;
 const pending = new Map();
 const requestFrame = fn => window.requestAnimationFrame ? window.requestAnimationFrame(fn) : setTimeout(() => fn(performance.now()), 16);
 const cancelFrame = id => window.cancelAnimationFrame ? window.cancelAnimationFrame(id) : clearTimeout(id);
 function tone(...args) { try { onBounce?.(...args); } catch { /* optional sound */ } }
function drawBoard(now){
 if(!ctx)return;
 ctx.clearRect(0,0,BOARD_W,BOARD_H);
 const g=geometry(game.rows);
 ctx.save();
 const glow=ctx.createRadialGradient(390,310,10,390,310,340);
 glow.addColorStop(0,"rgba(0,214,50,.035)");glow.addColorStop(1,"rgba(0,214,50,0)");
 ctx.fillStyle=glow;ctx.fillRect(0,0,BOARD_W,BOARD_H);
 ctx.strokeStyle="rgba(92,150,108,.055)";ctx.lineWidth=1;
 ctx.beginPath();ctx.moveTo(390-g.step*1.6,30);ctx.lineTo(390-(game.rows+2)*g.step/2-16,570);ctx.stroke();
 ctx.beginPath();ctx.moveTo(390+g.step*1.6,30);ctx.lineTo(390+(game.rows+2)*g.step/2+16,570);ctx.stroke();
 for(let r=0;r<game.rows;r++){
  for(let j=0;j<r+3;j++){
   const x=390+(j-(r+2)/2)*g.step,y=g.top+r*g.gap;
   ctx.fillStyle="rgba(0,0,0,.2)";ctx.beginPath();ctx.ellipse(x+1,y+3,4,3,0,0,Math.PI*2);ctx.fill();
   const dot=ctx.createRadialGradient(x-1,y-1,0,x,y,4);
   dot.addColorStop(0,"#d8e8dd");dot.addColorStop(.5,"#8aa995");dot.addColorStop(1,"#3f6a4e");
   ctx.fillStyle=dot;ctx.beginPath();ctx.arc(x,y,3.5,0,Math.PI*2);ctx.fill();
  }
 }
 impacts=impacts.filter(hit=>now-hit.at<380);
 impacts.forEach(hit=>{
  const t=(now-hit.at)/380;
  ctx.strokeStyle=`rgba(72,235,113,${(1-t)*.55})`;ctx.lineWidth=1.1;
  ctx.beginPath();ctx.arc(hit.x,hit.y,5+t*12,0,Math.PI*2);ctx.stroke();
  ctx.fillStyle=`rgba(116,255,151,${(1-t)*.8})`;ctx.beginPath();ctx.arc(hit.x,hit.y,3.9,0,Math.PI*2);ctx.fill();
 });
 landings=landings.filter(hit=>now-hit.at<650);
 landings.forEach(hit=>{
  const t=(now-hit.at)/650;
  const burst=ctx.createRadialGradient(hit.x,hit.y,0,hit.x,hit.y,18+28*t);
  burst.addColorStop(0,`rgba(0,214,50,${.36*(1-t)})`);burst.addColorStop(1,"rgba(0,214,50,0)");
  ctx.fillStyle=burst;ctx.beginPath();ctx.arc(hit.x,hit.y,18+28*t,0,Math.PI*2);ctx.fill();
 });
 visuals.forEach(visual=>{
  visual.trail.forEach((p,i)=>{
   ctx.fillStyle=`rgba(0,214,50,${i/Math.max(1,visual.trail.length)*.16})`;
   ctx.beginPath();ctx.arc(p.x,p.y,2.6+i/Math.max(1,visual.trail.length)*2,0,Math.PI*2);ctx.fill();
  });
  const p=visual.point;
  const halo=ctx.createRadialGradient(p.x,p.y,0,p.x,p.y,21);
  halo.addColorStop(0,"rgba(0,255,82,.35)");halo.addColorStop(1,"rgba(0,214,50,0)");
  ctx.fillStyle=halo;ctx.beginPath();ctx.arc(p.x,p.y,21,0,Math.PI*2);ctx.fill();
  const ball=ctx.createRadialGradient(p.x-2,p.y-3,.2,p.x,p.y,7);
  ball.addColorStop(0,"#c9ffcf");ball.addColorStop(.24,"#64ff88");ball.addColorStop(.64,"#00de40");ball.addColorStop(1,"#008c29");
  ctx.fillStyle=ball;ctx.beginPath();ctx.arc(p.x,p.y,6.5,0,Math.PI*2);ctx.fill();
 });
 ctx.restore();
}
 function finish(id) {
  const entry = pending.get(id); if (!entry) return;
  pending.delete(id); clearTimeout(entry.timer); entry.signal?.removeEventListener('abort', entry.abort);
  visuals = visuals.filter(item => item.row.request_id !== id);
  if (!reduced()) { const g = geometry(entry.round.rows); landings.push({ x: 390 + (entry.round.pocket - entry.round.rows / 2) * g.step, y: 582, at: performance.now() }); }
  drawBoard(performance.now()); entry.resolve(entry.round); wake();
 }
 function wake() { if (!disposed && !raf) raf = requestFrame(tick); }
 function tick(now) {
  raf = 0; if (disposed) return; const finished = [];
  for (const visual of visuals) {
   visual.elapsed = Math.max(0, now - visual.startedAt);
   if (visual.elapsed >= visual.flight.total) { finished.push(visual.row.request_id); continue; }
   const point = flightPoint(visual.flight, visual.elapsed); visual.point = point;
   visual.trail.push({ x: point.x, y: point.y }); if (visual.trail.length > 7) visual.trail.shift();
   if (point.index > visual.lastIndex) { const hit = visual.flight.points[point.index]; impacts.push({ x: hit.x, y: hit.y + 9, at: now }); visual.lastIndex = point.index; tone(390 + Math.min(250, point.index * 13)); }
  }
  finished.forEach(finish); drawBoard(now); if (visuals.length || impacts.length || landings.length) wake();
 }
 function drop(round, options = {}) {
  if (disposed || options.signal?.aborted) return Promise.reject(Object.assign(new Error('Aborted'), { name: 'AbortError' }));
  if (pending.has(round.request_id)) return pending.get(round.request_id).promise;
  if (pending.size >= 5 || round.rows !== game.rows) return Promise.reject(new Error('The board settings changed during a drop.'));
  let resolve, reject; const promise = new Promise((ok, fail) => { resolve = ok; reject = fail; });
  const entry = { round, promise, resolve, reject, signal: options.signal, timer: null, abort: null };
  entry.abort = () => { if (!pending.delete(round.request_id)) return; clearTimeout(entry.timer); visuals = visuals.filter(item => item.row.request_id !== round.request_id); reject(Object.assign(new Error('Aborted'), { name: 'AbortError' })); };
  pending.set(round.request_id, entry); entry.signal?.addEventListener('abort', entry.abort, { once: true });
  if (options.reduced || reduced()) entry.timer = setTimeout(() => finish(round.request_id), 140);
  else { const flight = pathFor(round); visuals.push({ row: round, flight, startedAt: performance.now(), elapsed: 0, lastIndex: 0, trail: [], point: flight.points[0] }); wake(); }
  return promise;
 }
 const preferenceChanged = () => { if (reduced()) { impacts = []; landings = []; [...pending.keys()].forEach(finish); } };
 media?.addEventListener?.('change', preferenceChanged);
 drawBoard(performance.now());
 return { available: true, drop, setRows(value) { if (!pending.size) { game.rows = value; impacts = []; landings = []; drawBoard(performance.now()); } },
  cancel() { disposed = true; if (raf) cancelFrame(raf); raf = 0; media?.removeEventListener?.('change', preferenceChanged); for (const entry of [...pending.values()]) entry.abort(); } };
}
